import { APP_BASE_HREF } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { CookieConsentService } from '../cookie-consent/cookie-consent-service';

@Injectable({ providedIn: 'root' })
export class ClientSessionService {
    private readonly knownCookieNames = [
        'pa-web_Patient_Local',
        'pa-web_Patient',
        'cookieconsent_status',
        'sessionTokenAuthAriadna',
        'cc_cookie',
    ];

    public constructor(
        @Inject(APP_BASE_HREF) private readonly baseHref: string,
        private readonly cookieService: CookieConsentService,
    ) {}

    public clear(): void {
        this.runSafely(
            () => localStorage.clear(),
            'Failed to clear localStorage',
        );
        this.runSafely(
            () => sessionStorage.clear(),
            'Failed to clear sessionStorage',
        );
        this.runSafely(
            () => this.clearCookies(),
            'Failed to clear local cookies',
        );
    }

    private clearCookies(): void {
        if (typeof document === 'undefined' || typeof window === 'undefined') {
            return;
        }

        const cookieNames = new Set(this.knownCookieNames);
        this.getAccessibleCookieNames().forEach((name) =>
            cookieNames.add(name),
        );

        const paths = this.getCookiePaths();
        const domains = this.getCookieDomains();

        cookieNames.forEach((name) =>
            this.deleteCookieForEveryScope(name, paths, domains),
        );

        paths.forEach((path) => {
            this.runSafely(
                () => this.cookieService.deleteAll(path),
                `[Cookie] Failed to delete cookies at path=${path}`,
            );
        });

        this.getAccessibleCookieNames().forEach((name) =>
            this.deleteCookieForEveryScope(name, paths, domains),
        );
    }

    private getAccessibleCookieNames(): string[] {
        try {
            return Object.keys(this.cookieService.getAllCookies());
        } catch (error) {
            console.warn('[Cookie] Failed to read local cookies', error);
            return [];
        }
    }

    private deleteCookieForEveryScope(
        name: string,
        paths: string[],
        domains: Array<string | undefined>,
    ): void {
        paths.forEach((path) => {
            this.runSafely(
                () => this.cookieService.deleteCookie(name, path),
                `[Cookie] Failed to delete ${name} at path=${path}`,
            );

            domains.forEach((domain) => {
                const domainPart = domain ? `; Domain=${domain}` : '';
                this.runSafely(
                    () => {
                        document.cookie = `${name}=; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=${path}${domainPart}`;
                    },
                    `[Cookie] Failed to delete ${name} at path=${path} domain=${domain ?? 'host-only'}`,
                );
            });
        });
    }

    private getCookiePaths(): string[] {
        const paths = new Set<string>(['/']);
        const basePath = this.normalizePath(this.baseHref);
        const currentPath = this.normalizePath(window.location.pathname);

        [
            basePath,
            currentPath,
            '/auth',
            '/login',
            '/home',
            `${basePath}/auth`,
            `${basePath}/login`,
            `${basePath}/home`,
        ].forEach((path) => this.addPathAndParents(paths, path));

        return [...paths];
    }

    private addPathAndParents(paths: Set<string>, rawPath: string): void {
        const path = this.normalizePath(rawPath);
        const segments = path.split('/').filter(Boolean);
        let parentPath = '';

        paths.add(path);
        if (path !== '/') paths.add(`${path}/`);

        segments.forEach((segment) => {
            parentPath += `/${segment}`;
            paths.add(parentPath);
            paths.add(`${parentPath}/`);
        });
    }

    private normalizePath(path: string | null | undefined): string {
        const normalized = `/${path ?? ''}`
            .replace(/\/+/g, '/')
            .replace(/\/$/, '');
        return normalized || '/';
    }

    private getCookieDomains(): Array<string | undefined> {
        const hostname = window.location.hostname;
        const domains = new Set<string | undefined>([undefined]);

        if (!hostname) return [...domains];

        domains.add(hostname);
        domains.add(`.${hostname}`);

        if (!/^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) {
            const hostnameParts = hostname.split('.');
            for (let index = 1; index < hostnameParts.length - 1; index++) {
                const parentDomain = hostnameParts.slice(index).join('.');
                domains.add(parentDomain);
                domains.add(`.${parentDomain}`);
            }
        }

        return [...domains];
    }

    private runSafely(action: () => void, warning: string): void {
        try {
            action();
        } catch (error) {
            console.warn(warning, error);
        }
    }
}
